import { useEffect } from 'react'
import { Link, useParams, Navigate } from 'react-router-dom'
import { CheckCircle, ChevronRight, MapPin } from 'lucide-react'
import { getCityBySlug, getNearbyCities } from '@/data/ukCities'
import SiluxPromise from '@/components/SiluxPromise'
import { setMetaTags, injectSchema } from '@/utils/seo'

const PLANS = [
  { name: "Broadband Lite", data: "Unlimited", price: "£15.95", features: ["Unlimited data", "4G/5G router included", "24-month plan — best rate", "Free delivery"] },
  { name: "5G Premium", data: "Unlimited 5G", price: "£19.95", features: ["Unlimited 5G priority", "5G router included", "36-month plan (12-month £27.95)", "Free delivery"], popular: true },
  { name: "MiFi Portable", data: "Take it anywhere", price: "£15.95", features: ["Pocket-sized MiFi included", "Runs on battery", "24-month plan (12-month £17.50)", "Free delivery"] },
]

export default function GeoBroadband() {
  const { city: citySlug } = useParams<{ city: string }>()
  const city = getCityBySlug(citySlug ?? '')

  if (!city) return <Navigate to="/mobile-broadband" replace />

  const nearby = getNearbyCities(city)
  const isScotland = city.region === 'Scotland'
  const isWales = city.region === 'Wales'
  const isNI = city.region === 'Northern Ireland'
  const regionLabel = isScotland ? 'Scotland' : isWales ? 'Wales' : isNI ? 'Northern Ireland' : 'England'

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        "name": `Mobile Broadband in ${city.name}`,
        "provider": { "@type": "Organization", "name": "Silux Mobile", "url": "https://www.siluxmobile.co.uk" },
        "serviceType": "Mobile Broadband",
        "areaServed": { "@type": "City", "name": city.name, "containedInPlace": { "@type": "Country", "name": "United Kingdom" } },
        "description": `4G and 5G mobile broadband in ${city.name}, ${city.county}. No landline and no engineer needed, router included and delivered. From £15.95/month on the Three, O2 and Vodafone networks, with plans from 1 month (rolling) to 36 months.`,
        "url": `https://www.siluxmobile.co.uk/mobile-broadband/${city.slug}`,
        "offers": { "@type": "AggregateOffer", "lowPrice": "15.95", "highPrice": "32.95", "priceCurrency": "GBP" }
      },
      {
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": `Can I get rolling monthly broadband in ${city.name}?`,
            "acceptedAnswer": { "@type": "Answer", "text": `Yes. Broadband Lite (from £25.95/month) and 5G Premium (from £38.95/month) are available in ${city.name} on a rolling monthly contract — a 1-month minimum term, then month to month until you give 30 days’ notice. All our plans start from 1 month; fixed terms of 3 to 36 months cost less per month.` }
          },
          {
            "@type": "Question",
            "name": `What is the best mobile broadband in ${city.name}?`,
            "acceptedAnswer": { "@type": "Answer", "text": `Silux Mobile offers the best value mobile broadband in ${city.name} — from £15.95/month on a 24-month plan, with the router included, no landline required and no engineer visit. Every plan comes with unlimited data, on a rolling monthly contract or a fixed term of 3, 6, 12, 18, 24 or 36 months.` }
          },
          {
            "@type": "Question",
            "name": `Can I get broadband in ${city.name} without a landline?`,
            "acceptedAnswer": { "@type": "Answer", "text": `Yes. Silux Mobile's mobile broadband in ${city.name} requires no landline, no phone line and no engineer visit. Simply insert a SIM into a 4G/5G router and you have broadband. Works in rented flats, shared houses and any property with Three's signal in ${city.county}.` }
          },
          {
            "@type": "Question",
            "name": `How fast is mobile broadband in ${city.name}?`,
            "acceptedAnswer": { "@type": "Answer", "text": `On Three's 4G network in ${city.name}, typical speeds are 10–50 Mbps on Broadband Lite and 50–150 Mbps on 5G Premium. In 5G coverage areas of ${city.name} and ${city.county}, speeds are 80–400 Mbps — fast enough for HD streaming, video calls, gaming and working from home.` }
          },
          {
            "@type": "Question",
            "name": `Is mobile broadband cheaper than fibre in ${city.name}?`,
            "acceptedAnswer": { "@type": "Answer", "text": `In most cases, yes. Mobile broadband from Silux Mobile starts at £15.95/month with the router included and no installation to pay for. Typical fibre deals in ${city.name} cost £25–45/month. Mobile broadband is especially good value if you rent or move frequently in ${city.county}, because it comes with you to the next address.` }
          },
          {
            "@type": "Question",
            "name": `Will my broadband price increase during my contract?`,
            "acceptedAnswer": { "@type": "Answer", "text": `No. Your price is fixed for the life of your plan — there are no price increases during your contract and no annual inflation-linked price rises. What you sign up at in ${city.name} is what you pay.` }
          },
          {
            "@type": "Question",
            "name": `Are broadband upgrades free?`,
            "acceptedAnswer": { "@type": "Answer", "text": `Yes. Silux Mobile offers free broadband upgrades — move to a faster plan or upgrade your router with no upgrade fee. Free upgrades are included as standard for ${city.name} customers.` }
          },
          {
            "@type": "Question",
            "name": `Who is the best mobile broadband provider in ${city.name}?`,
            "acceptedAnswer": { "@type": "Answer", "text": `Silux Mobile is a leading mobile broadband and fast internet provider in ${city.name}, delivering 4G and 5G home broadband on the Three, O2 and Vodafone networks — no landline, no engineer, router included, from £15.95/month.` }
          },
          {
            "@type": "Question",
            "name": `Are Silux Mobile broadband prices in ${city.name} inclusive of VAT?`,
            "acceptedAnswer": { "@type": "Answer", "text": `Yes. There are no hidden fees on Silux Mobile broadband plans — the price displayed to ${city.name} customers is the price you pay, incl. VAT, with no setup fee and no engineer fee. Short plans take a small refundable holding deposit (£15 on 3-month plans, £30 on rolling monthly Lite), which you get back.` }
          }
        ]
      },
      {
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.siluxmobile.co.uk" },
          { "@type": "ListItem", "position": 2, "name": "Mobile Broadband", "item": "https://www.siluxmobile.co.uk/mobile-broadband" },
          { "@type": "ListItem", "position": 3, "name": city.name, "item": `https://www.siluxmobile.co.uk/mobile-broadband/${city.slug}` }
        ]
      }
    ]
  }

  useEffect(() => {
    const canonical = `https://www.siluxmobile.co.uk/mobile-broadband/${city.slug}`
    setMetaTags({
      title: `Mobile Broadband Provider in ${city.name} | Fast Internet Provider | Silux Mobile`,
      description: `Silux Mobile is a mobile broadband provider and fast internet provider in ${city.name}. 4G & 5G home broadband from £15.95/month — no landline, no engineer, router included. Plans from 1 month rolling to 36 months on the Three, O2 and Vodafone networks covering ${city.county}. Order today.`,
      keywords: [`mobile broadband ${city.name}`, `home broadband ${city.name}`, `4g 5g broadband ${city.name}`, `internet provider ${city.name}`, 'mobile broadband uk', 'no landline broadband', 'broadband without a landline'],
      canonical,
      ogUrl: canonical,
    })
    injectSchema(JSON.stringify(schema), 'geo-bb-schema')
    return () => { const el = document.getElementById('geo-bb-schema'); if (el) el.remove() }
  }, [city.slug])

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="bg-gradient-to-br from-teal-700 to-teal-900 text-white py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <nav className="text-teal-300 text-sm mb-6 flex items-center gap-2 flex-wrap">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/mobile-broadband" className="hover:text-white">Mobile Broadband</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-white">{city.name}</span>
          </nav>
          <div className="flex items-center gap-2 mb-3">
            <MapPin className="w-5 h-5 text-teal-300" />
            <span className="text-teal-300 text-sm">{city.county}, {regionLabel}</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Mobile Broadband in {city.name}
          </h1>
          <p className="text-xl text-teal-100 mb-3 max-w-2xl">
            Silux Mobile is a mobile broadband provider and fast internet provider in {city.name}, with 4G and 5G home broadband from £15.95/month and the router included. No landline, no engineer, no setup fee. Works in any property with Three, O2 or Vodafone signal across {city.county}.
          </p>
          <p className="text-teal-200 text-sm mb-8 max-w-2xl">
            <strong>Mobile broadband provider in {city.name}</strong> — Silux Mobile uses Three's 5G and 4G network to deliver internet to your home or office without a phone line. As a {city.name} broadband provider with no landline, we're perfect for renters and anyone who wants internet on their own terms — pick a plan from 1 month rolling to 36 months and take it with you if you move. Plug in the SIM, connect your devices — done.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link to="/broadband" className="bg-white text-teal-800 font-semibold px-6 py-3 rounded-lg hover:bg-teal-50 transition">
              See Broadband Plans
            </Link>
            <Link to="/network-coverage" className="border border-white text-white px-6 py-3 rounded-lg hover:bg-white/10 transition">
              Check {city.name} Coverage
            </Link>
          </div>
        </div>
      </section>

      {/* Key benefits */}
      <section className="py-14 px-4 bg-teal-50">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Mobile Broadband in {city.name} — Key Benefits</h2>
          <div className="grid md:grid-cols-4 gap-5">
            {[
              { title: "No Landline Needed", body: `Works in any ${city.name} property — flats, rented houses, shared homes. No BT line required.`, icon: "📡" },
              { title: "No Engineer Visit", body: `No waiting weeks for an engineer. Free Royal Mail Tracked 48 delivery (2–3 working days), or faster at checkout. Broadband in ${city.name} the day it arrives.`, icon: "⚡" },
              { title: "You Pick The Term", body: "From 1 month rolling to 36 months — and it moves with you around " + city.name + ", which suits rented accommodation.", icon: "🔓" },
              { title: "Three 5G Network", body: `Three's 5G covers most of ${city.name} city centre and is expanding across ${city.county}.`, icon: "📶" },
            ].map(c => (
              <div key={c.title} className="bg-white rounded-xl p-5 shadow-sm text-center">
                <div className="text-3xl mb-3">{c.icon}</div>
                <h3 className="font-bold text-gray-900 text-sm mb-2">{c.title}</h3>
                <p className="text-gray-500 text-xs">{c.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <SiluxPromise variant="broadband" />

      {/* Plans */}
      <section className="py-16 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 mb-2 text-center">Broadband Plans for {city.name}</h2>
          <p className="text-gray-500 text-center mb-10">No landline. No engineer. No setup fee. Works anywhere in {city.county} with coverage.</p>
          <div className="grid md:grid-cols-3 gap-6">
            {PLANS.map(plan => (
              <div key={plan.name} className={`rounded-2xl p-6 border-2 ${plan.popular ? 'border-teal-500 shadow-lg' : 'border-gray-200'} relative`}>
                {plan.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-teal-600 text-white text-xs font-bold px-3 py-1 rounded-full">MOST POPULAR</div>}
                <h3 className="text-xl font-bold mb-1">{plan.name}</h3>
                <p className="text-sm text-gray-500 mb-2">{plan.data}</p>
                <p className="text-3xl font-bold text-teal-700 mb-4">{plan.price}<span className="text-sm font-normal text-gray-500">/mo</span></p>
                <ul className="space-y-2 mb-6">
                  {plan.features.map(f => (
                    <li key={f} className="flex items-center gap-2 text-sm text-gray-700">
                      <CheckCircle className="w-4 h-4 text-teal-500 flex-shrink-0" /> {f}
                    </li>
                  ))}
                </ul>
                <Link to="/broadband" className={`block text-center py-2.5 rounded-lg font-semibold text-sm transition ${plan.popular ? 'bg-teal-600 text-white hover:bg-teal-700' : 'bg-gray-100 text-gray-800 hover:bg-gray-200'}`}>
                  Get This Plan
                </Link>
              </div>
            ))}
          </div>
          <p className="text-center text-sm text-gray-500 mt-6">
            Plans start from 1 month: rolling monthly Broadband Lite is £25.95/month and 5G Premium £38.95/month (1-month minimum, 30 days’ notice), and 3-month plans start at £19.95/month plus a £15 refundable holding deposit. No setup fee, whichever you choose.
          </p>
        </div>
      </section>

      {/* Speed info */}
      <section className="py-12 px-4 bg-gray-50">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Mobile Broadband Speeds in {city.name}</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm bg-white rounded-xl overflow-hidden shadow-sm">
              <thead className="bg-teal-700 text-white">
                <tr>
                  <th className="px-4 py-3 text-left">What you do online</th>
                  <th className="px-4 py-3 text-center">Speed needed</th>
                  <th className="px-4 py-3 text-center">Three 4G in {city.name}</th>
                  <th className="px-4 py-3 text-center">Three 5G in {city.name}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {[
                  ["Browsing & email",       "2 Mbps",  "✅ 10–150 Mbps",  "✅ 80–400 Mbps"],
                  ["HD streaming",           "5 Mbps",  "✅ Yes",           "✅ Yes"],
                  ["4K streaming",           "25 Mbps", "✅ Most areas",    "✅ Always"],
                  ["Video calls / Zoom",     "3 Mbps",  "✅ Yes",           "✅ Yes"],
                  ["Working from home",      "10 Mbps", "✅ Yes",           "✅ Yes"],
                  ["Gaming",                 "20 Mbps", "✅ Good",          "✅ Excellent"],
                ].map(([a, b, c, d]) => (
                  <tr key={a} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{a}</td>
                    <td className="px-4 py-3 text-center text-gray-500">{b}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{c}</td>
                    <td className="px-4 py-3 text-center text-gray-700">{d}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-gray-900 mb-8 text-center">Mobile Broadband {city.name} — FAQs</h2>
          <div className="space-y-3">
            {(schema["@graph"][1] as any).mainEntity.map((q: any) => (
              <details key={q.name} className="bg-gray-50 rounded-xl border border-gray-200 p-5 group">
                <summary className="font-semibold text-gray-900 cursor-pointer list-none flex justify-between items-center text-sm">
                  {q.name}
                  <ChevronRight className="w-4 h-4 text-gray-400 group-open:rotate-90 transition-transform flex-shrink-0 ml-2" />
                </summary>
                <p className="text-gray-600 text-sm mt-3">{q.acceptedAnswer.text}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Nearby cities */}
      {nearby.length > 0 && (
        <section className="py-12 px-4 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-lg font-bold text-gray-900 mb-4 text-center">Mobile Broadband Near {city.name}</h2>
            <div className="flex flex-wrap justify-center gap-3">
              {nearby.map(c => (
                <Link key={c.slug} to={`/mobile-broadband/${c.slug}`} className="bg-white border border-gray-200 rounded-lg px-4 py-2 text-sm text-gray-700 hover:border-teal-400 hover:text-teal-700 transition">
                  {c.name}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Cross-links */}
      <section className="py-12 px-4 bg-teal-700 text-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-lg font-bold mb-4 text-center">More from Silux Mobile</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[
              { to: "/rolling-monthly-broadband/" + city.slug, label: `Rolling Monthly Broadband in ${city.name}` },
              { to: "/cheap-broadband",            label: "Cheap Broadband" },
              { to: `/cheap-sim-cards/${city.slug}`, label: `SIM Cards in ${city.name}` },
              { to: "/broadband-renters",           label: "Broadband for Renters" },
              { to: "/broadband-rural-uk",          label: "Rural Broadband" },
            ].map(l => (
              <Link key={l.to} to={l.to} className="bg-white/10 hover:bg-white/20 rounded-lg px-4 py-3 text-center text-sm font-medium transition">
                {l.label}
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  )
}
